import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { commentAPI, serverAPI, type CommentRecord } from '../utils/api';
import ServerInfoPanel from './ServerInfoPanel';
import EventSchedule from './EventSchedule';
import Leaderboard from './Leaderboard';
import ScreenshotCarousel from './ScreenshotCarousel';
import ServerRules from './ServerRules';
import ServerStaff from './ServerStaff';
import FAQ from './FAQ';
import RelatedServers from './RelatedServers';
import ServerReviews, { ServerReview } from './ServerReviews';
import ServerReviewForm from './ServerReviewForm';
import CategoryRating from './CategoryRating';
import { useRealtimeEvent } from '../hooks/useRealtimeEvent';
import type { RealtimeComment, RealtimeServer } from '../types/realtime';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

// Interfaces
interface ServerEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  recurring?: 'daily' | 'weekly' | 'monthly';
}

interface LeaderboardEntry {
  id: number;
  rank: number;
  playerName: string;
  guild?: string;
  score: number;
  avatar?: string;
}

interface Screenshot {
  id: string;
  url: string;
  title: string;
  description?: string;
}

interface Rule {
  id: number;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
}

interface StaffMember {
  id: number;
  name: string;
  role: string;
  avatar?: string;
  contact?: string;
}

interface DonationOption {
  id: number;
  title: string;
  description: string;
  price: string;
  benefits: string[];
  featured?: boolean;
}

interface ChangelogEntry {
  id: number;
  version: string;
  date: string;
  changes: {
    type: 'added' | 'fixed' | 'changed' | 'removed';
    description: string;
  }[];
}

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface RelatedServer {
  id: number;
  title: string;
  description: string;
  launchDate: string;
  banner: string;
  rating: number;
  status: 'online' | 'offline' | 'maintenance' | 'starting';
}

interface SocialMediaLink {
  id: number;
  name: string;
  url: string;
  icon: React.ReactNode;
}

interface SystemRequirement {
  id: number;
  category: string;
  requirements: {
    name: string;
    value: string;
  }[];
}

type ServerStatusType = 'online' | 'offline' | 'maintenance' | 'starting';

interface Server {
  id: string;
  title: string;
  description: string;
  launchDate: string;
  downloadLink: string;
  homepageUrl?: string;
  discordLink?: string;
  banner: string;
  rating: number;
  status: ServerStatusType;
  playersOnline?: number;
  maxPlayers?: number;
  version?: string;
  tags?: string[];
  ownerId?: string;
  events?: ServerEvent[];
  leaderboard?: LeaderboardEntry[];
  screenshots?: Screenshot[];
  bossContent?: Screenshot[];
  bossContentTitle?: string;
  itemTransformations?: Screenshot[];
  itemContentTitle?: string;
  partners?: { name: string; url: string; iconUrl: string }[];
  rules?: Rule[];
  staff?: StaffMember[];
  donations?: DonationOption[];
  changelog?: ChangelogEntry[];
  faq?: FAQItem[];
  relatedServers?: RelatedServer[];
  socialMedia?: SocialMediaLink[];
  systemRequirements?: SystemRequirement[];
  onlineRating?: number;
  managementRating?: number;
  gameplayRating?: number;
}

type CommentSource = CommentRecord | RealtimeComment;

const ensureIsoString = (value: string | Date | undefined): string => {
  if (!value) {
    return new Date().toISOString();
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
};

const mapCommentToReview = (comment: CommentSource): ServerReview => {
  const helpfulCount =
    typeof (comment as any).helpful_count === 'number'
      ? (comment as any).helpful_count
      : typeof (comment as CommentRecord).helpfulCount === 'number'
        ? (comment as CommentRecord).helpfulCount
        : Array.isArray(comment.helpfulUserIds)
          ? comment.helpfulUserIds.length
          : 0;

  const notHelpfulCount =
    typeof (comment as any).not_helpful_count === 'number'
      ? (comment as any).not_helpful_count
      : typeof (comment as CommentRecord).notHelpfulCount === 'number'
        ? (comment as CommentRecord).notHelpfulCount
        : Array.isArray(comment.notHelpfulUserIds)
          ? comment.notHelpfulUserIds.length
          : 0;

  return {
    id: comment.id,
    rating: comment.rating,
    content: comment.content,
    createdAt: ensureIsoString(comment.createdAt),
    updatedAt: ensureIsoString(comment.updatedAt),
    helpfulCount,
    notHelpfulCount,
    user: comment.user
      ? {
        id: comment.user.id,
        username: comment.user.username,
        displayName: comment.user.displayName ?? null,
        avatarUrl: comment.user.avatarUrl ?? null,
      }
      : undefined,
  };
};

const sortReviewsByDate = (reviews: ServerReview[]) =>
  [...reviews].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

const ServerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();

  const [server, setServer] = useState<Server | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reviews, setReviews] = useState<ServerReview[]>([]);
  const [isReviewsLoading, setIsReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);
  const [pendingVoteIds, setPendingVoteIds] = useState<Set<string>>(new Set());
  const [reviewSubmitError, setReviewSubmitError] = useState<string | null>(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  // Fetch Server Details and Favorites
  useEffect(() => {
    if (!id) {
      setError(t('server.details.error_missing_id') || 'Server identifier missing.');
      setLoading(false);
      return;
    }

    let isMounted = true;

    setReviewSubmitError(null);
    setIsSubmittingReview(false);

    const fetchData = async () => {
      setLoading(true);
      try {
        const [serverParams, favoritesParams] = await Promise.all([
          serverAPI.getServerById(id),
          isAuthenticated ? serverAPI.getFavorites() : Promise.resolve({ data: [] })
        ]);

        const response = serverParams;
        const favorites = favoritesParams.data || [];

        if (!isMounted) {
          return;
        }

        setIsFavorite(favorites.some((fav: any) => fav.id === id));

        const mappedServer: Server = {
          id: response.id,
          title: response.name,
          description: response.description,
          launchDate: response.createdAt,
          downloadLink: response.downloadUrl || '',
          homepageUrl: response.websiteUrl,
          discordLink: response.discordLink,
          banner: (response as any).bannerUrl || '',
          rating: response.ratingAverage,
          status: response.status as ServerStatusType,
          playersOnline: response.currentPlayers,
          maxPlayers: response.maxPlayers,
          version: response.game,
          tags: response.tags,
          ownerId: response.user?.id,
          events: [],
          leaderboard: [],
          screenshots: response.screenshots?.map((s: any) => ({
            id: s.id,
            url: s.url,
            title: s.caption || '',
            description: ''
          })) || [],
          bossContent: response.bossContent?.map((s: any) => ({
            id: s.id,
            url: s.url,
            title: s.caption || '',
            description: ''
          })) || [],
          itemTransformations: response.itemTransformations?.map((s: any) => ({
            id: s.id,
            url: s.url,
            title: s.caption || '',
            description: ''
          })) || [],
          bossContentTitle: response.bossContentTitle,
          itemContentTitle: response.itemContentTitle,
          partners: response.partners || [],
          rules: [],
          staff: [],
          donations: [],
          changelog: [],
          faq: [],
          relatedServers: [],
          socialMedia: [],
          systemRequirements: [],
          onlineRating: response.onlineRating,
          managementRating: response.managementRating,
          gameplayRating: response.gameplayRating,
        };

        setServer(mappedServer);
        setError(null);
      } catch (fetchError) {
        console.error('Failed to load server from backend', fetchError);
        if (!isMounted) {
          return;
        }
        setError(t('server.details.error_loading') || 'Unable to load server details.');
        setServer(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id, t, isAuthenticated]);

  // Fetch Reviews
  useEffect(() => {
    if (!id) {
      return;
    }

    let isCancelled = false;

    const loadReviews = async () => {
      setIsReviewsLoading(true);
      setReviewsError(null);
      setPendingVoteIds(new Set());

      try {
        const response = await commentAPI.getForServer(id, { page: 1, limit: 50 });
        if (isCancelled) {
          return;
        }

        const mapped = response.data.map(mapCommentToReview);
        setReviews(sortReviewsByDate(mapped));
      } catch (loadError) {
        console.error('Failed to load reviews for server', loadError);
        if (isCancelled) {
          return;
        }

        setReviewsError(t('server.reviews.error_loading') || 'Unable to load player reviews right now.');
        setReviews([]);
      } finally {
        if (!isCancelled) {
          setIsReviewsLoading(false);
        }
      }
    };

    loadReviews();

    return () => {
      isCancelled = true;
    };
  }, [id, t]);

  const handleVote = useCallback(
    async (reviewId: string, vote: 'helpful' | 'not_helpful') => {
      if (!id) {
        return;
      }

      if (!user) {
        setReviewsError(t('server.reviews.error_login_vote') || 'You need to sign in to vote on reviews.');
        return;
      }

      setPendingVoteIds((prev) => {
        const next = new Set(prev);
        next.add(reviewId);
        return next;
      });

      try {
        const updatedComment = await commentAPI.voteOnComment(reviewId, vote);
        const mappedReview = mapCommentToReview(updatedComment);
        setReviews((previous) => {
          const index = previous.findIndex((review) => review.id === mappedReview.id);
          if (index === -1) {
            return sortReviewsByDate([...previous, mappedReview]);
          }

          const next = [...previous];
          next[index] = mappedReview;
          return sortReviewsByDate(next);
        });
        setReviewsError(null);
      } catch (voteError) {
        console.error('Failed to register vote', voteError);
        setReviewsError(t('server.reviews.error_vote') || 'Unable to register your vote right now. Please try again later.');
      } finally {
        setPendingVoteIds((prev) => {
          if (!prev.has(reviewId)) {
            return prev;
          }
          const next = new Set(prev);
          next.delete(reviewId);
          return next;
        });
      }
    },
    [id, user, t]
  );

  const handleCreateReview = useCallback(
    async ({ rating, content }: { rating: number; content: string }) => {
      if (!id) {
        return false;
      }

      if (!user) {
        setReviewSubmitError(t('server.reviews.error_login_review') || 'You need to sign in to leave a review.');
        return false;
      }

      setReviewSubmitError(null);
      setIsSubmittingReview(true);

      try {
        const createdComment = await commentAPI.createComment({ serverId: id, rating, content });
        const mappedReview = mapCommentToReview(createdComment);
        setReviews((previous) => {
          const existingIndex = previous.findIndex((reviewItem) => reviewItem.id === mappedReview.id);
          if (existingIndex !== -1) {
            const next = [...previous];
            next[existingIndex] = mappedReview;
            return sortReviewsByDate(next);
          }

          return sortReviewsByDate([...previous, mappedReview]);
        });
        setReviewsError(null);
        return true;
      } catch (createError) {
        console.error('Failed to create review', createError);
        setReviewSubmitError(t('server.reviews.error_submit') || 'Unable to submit your review right now. Please try again later.');
        return false;
      } finally {
        setIsSubmittingReview(false);
      }
    },
    [id, user, t]
  );

  const handleCommentCreated = useCallback(
    ({ comment }: { comment: RealtimeComment }) => {
      if (!id || comment.serverId !== id) {
        return;
      }

      const mappedReview = mapCommentToReview(comment);
      setReviewsError(null);
      setReviews((previous) => {
        const index = previous.findIndex((review) => review.id === mappedReview.id);
        if (index !== -1) {
          const next = [...previous];
          next[index] = mappedReview;
          return sortReviewsByDate(next);
        }

        return sortReviewsByDate([...previous, mappedReview]);
      });
    },
    [id]
  );

  const handleCommentVoted = useCallback(
    ({ comment }: { comment: RealtimeComment }) => {
      if (!id || comment.serverId !== id) {
        return;
      }

      const mappedReview = mapCommentToReview(comment);
      setReviews((previous) => {
        const index = previous.findIndex((review) => review.id === mappedReview.id);
        if (index === -1) {
          return previous;
        }

        const next = [...previous];
        next[index] = mappedReview;
        return sortReviewsByDate(next);
      });
    },
    [id]
  );

  const handleCommentDeleted = useCallback(
    ({ commentId, serverId }: { commentId: string; serverId: string }) => {
      if (!id || serverId !== id) {
        return;
      }

      setReviews((previous) => previous.filter((review) => review.id !== commentId));
      setPendingVoteIds((prev) => {
        if (!prev.has(commentId)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
    },
    [id]
  );

  const handleServerUpdated = useCallback(
    ({ server: updatedServer }: { server: RealtimeServer }) => {
      if (!id || updatedServer.id !== id) {
        return;
      }

      setServer((previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,
          title: updatedServer.name ?? previous.title,
          description: updatedServer.description ?? previous.description,
          downloadLink: updatedServer.downloadUrl ?? previous.downloadLink,
          homepageUrl: updatedServer.websiteUrl ?? previous.homepageUrl,
          rating:
            typeof updatedServer.ratingAverage === 'number'
              ? updatedServer.ratingAverage
              : previous.rating,
          status: (updatedServer.status as ServerStatusType) ?? previous.status,
          playersOnline: updatedServer.currentPlayers ?? previous.playersOnline,
          maxPlayers: updatedServer.maxPlayers ?? previous.maxPlayers,
          version: updatedServer.game ?? previous.version,
          tags:
            updatedServer.tags && updatedServer.tags.length > 0 ? updatedServer.tags : previous.tags,
        };
      });
    },
    [id]
  );

  useRealtimeEvent('comment:created', handleCommentCreated);
  useRealtimeEvent('comment:voted', handleCommentVoted);
  useRealtimeEvent('comment:deleted', handleCommentDeleted);
  useRealtimeEvent('server:updated', handleServerUpdated);

  const currentUserReview = useMemo(() => {
    if (!user) {
      return undefined;
    }

    return reviews.find((reviewItem) => reviewItem.user?.id === user.id);
  }, [reviews, user]);

  const handleDownload = () => {
    if (server?.downloadLink) {
      window.open(server.downloadLink, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 px-4 text-center text-gray-600 dark:text-gray-300">
        {t('server.details.loading') || 'Loading server details...'}
      </div>
    );
  }

  if (error || !server) {
    return (
      <div className="container mx-auto py-8 px-4 text-center text-red-600 dark:text-red-400">
        {error || t('server.details.not_found') || 'Server not found.'}
        <div className="mt-4">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
          >
            {t('server.details.back_home') || 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      {/* Header Section */}
      <ServerInfoPanel
        title={server.title}
        description={server.description}
        rating={server.rating}
        status={server.status}
        launchDate={new Date(server.launchDate).toLocaleDateString()}
        playersOnline={server.playersOnline}
        maxPlayers={server.maxPlayers}
        version={server.version}
        tags={server.tags}
        socialMedia={server.socialMedia}
        homepageUrl={server.homepageUrl}
        downloadLink={server.downloadLink}
        discordLink={server.discordLink}
        banner={server.banner}
        onDownload={handleDownload}
        isFavorite={isFavorite}
        serverId={server.id}
        canEdit={user?.role === 'admin' || (user?.id && user.id === server.ownerId)}
        onEdit={() => navigate(`/servers/${server.id}/edit`)}
      />

      {/* Main Content - New Layout */}
      <div className="mt-8 space-y-8">
        {/* Top Row: About (left) + Screenshots (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* About Section - Top Left */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('server.details.about') || 'About This Server'}</h2>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
              {server.description}
            </p>

            {/* Category Ratings */}
            {(server.onlineRating && server.onlineRating > 0) ||
              (server.managementRating && server.managementRating > 0) ||
              (server.gameplayRating && server.gameplayRating > 0) ? (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  {t('server.rating.category_ratings') || 'Kategori Derecelendirmeleri'}
                </h3>
                <div className="flex flex-col gap-2">
                  <CategoryRating category="online" rating={server.onlineRating || 0} size="md" />
                  <CategoryRating category="management" rating={server.managementRating || 0} size="md" />
                  <CategoryRating category="gameplay" rating={server.gameplayRating || 0} size="md" />
                </div>
              </div>
            ) : null}
          </div>

          {/* Screenshots Carousel - Top Right */}
          {server.screenshots && server.screenshots.length > 0 ? (
            <ScreenshotCarousel screenshots={server.screenshots} title={t('server.details.screenshots') || 'Screenshots'} />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-500">{t('server.details.no_screenshots') || 'No screenshots available'}</p>
            </div>
          )}
        </div>

        {/* Second Row: Item Transformations + Boss Content (side by side) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Item Transformations */}
          {server.itemTransformations && server.itemTransformations.length > 0 ? (
            <ScreenshotCarousel screenshots={server.itemTransformations} title={server.itemContentTitle || t('server.details.item_transformations') || 'Item Transformations'} />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center min-h-[200px]">
              <p className="text-gray-500 dark:text-gray-500">{t('server.details.no_item_transformations') || 'No item transformations'}</p>
            </div>
          )}

          {/* Boss Content */}
          {server.bossContent && server.bossContent.length > 0 ? (
            <ScreenshotCarousel screenshots={server.bossContent} title={server.bossContentTitle || t('server.details.boss_content') || 'Boss Content'} />
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center min-h-[200px]">
              <p className="text-gray-500 dark:text-gray-500">{t('server.details.no_boss_content') || 'No boss content'}</p>
            </div>
          )}
        </div>

        {/* Publishers & Advertisers Banner Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('server.details.publishers_advertisers') || 'Yayıncılar ve Reklamcılar'}</h2>
          {server.partners && server.partners.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {server.partners.map((partner, index) => (
                <a
                  key={index}
                  href={partner.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center p-4 bg-gray-100 dark:bg-gray-700/50 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                >
                  <img src={partner.iconUrl} alt={partner.name} className="w-16 h-16 object-contain mb-2" />
                  <span className="text-gray-700 dark:text-gray-300 font-medium text-sm text-center">{partner.name}</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="h-32 bg-gray-100 dark:bg-gray-700/30 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-500">{t('server.details.no_publishers') || 'Henüz yayıncı veya reklamcı eklenmemiş'}</p>
            </div>
          )}
        </div>

        {/* Events Schedule */}
        {server.events && server.events.length > 0 && (
          <EventSchedule events={server.events} />
        )}

        {/* Full-width Reviews Section */}
        <div id="reviews" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('server.reviews.title') || 'Player Reviews'}</h2>
            <span className="text-gray-500 dark:text-gray-400 text-sm">
              {reviews.length} {reviews.length === 1 ? (t('server.reviews.review_singular') || 'review') : (t('server.reviews.review_plural') || 'reviews')}
            </span>
          </div>

          {reviewsError && (
            <div className="bg-red-100 dark:bg-red-900/50 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-200 p-4 rounded-lg">
              {reviewsError}
            </div>
          )}

          {isAuthenticated && !currentUserReview && (
            <ServerReviewForm
              onSubmit={handleCreateReview}
              isSubmitting={isSubmittingReview}
              errorMessage={reviewSubmitError}
              currentUser={user}
              isAuthenticated={isAuthenticated}
            />
          )}

          {isReviewsLoading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('server.reviews.loading') || 'Loading reviews...'}</div>
          ) : (
            <ServerReviews
              reviews={reviews}
              onVote={handleVote}
              currentUserId={user?.id}
              pendingVoteIds={pendingVoteIds}
            />
          )}
        </div>

        {/* Additional Sidebar Content (moved to bottom as optional sections) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Leaderboard */}
          {server.leaderboard && server.leaderboard.length > 0 && (
            <Leaderboard entries={server.leaderboard} title={t('server.details.leaderboard') || 'Leaderboard'} />
          )}

          {/* Server Rules */}
          {server.rules && server.rules.length > 0 && (
            <ServerRules rules={server.rules} />
          )}

          {/* Staff Members */}
          {server.staff && server.staff.length > 0 && (
            <ServerStaff staff={server.staff} />
          )}

          {/* FAQ */}
          {server.faq && server.faq.length > 0 && (
            <FAQ items={server.faq} />
          )}
        </div>

        {/* Related Servers */}
        {server.relatedServers && server.relatedServers.length > 0 && (
          <RelatedServers servers={server.relatedServers} />
        )}
      </div>
    </div >
  );
};

export default ServerDetails;